/**
 * Logbook Repository
 * 
 * Complete data access layer for logbook entries
 * Handles both Dexie/IndexedDB (offline) and Supabase (sync)
 * 
 * Features:
 * - Full CRUD operations
 * - Advanced queries (by ship, type, date range, watch period)
 * - Bulk operations for importing historical data
 * - Workflow state management (draft -> reviewed -> approved -> locked)
 * - Audit trail integration
 * 
 * @module repositories/LogbookRepository
 */

import { BaseRepository, QueryOptions, QueryResult } from '../BaseRepository';
import { db } from '../db';
import type {
  LogbookEntry,
  LogbookType,
  LogbookEntryStatus,
  LogbookFilters
} from '../../../types/logbook';
import { createLogger } from '../../utils/logger';

const logger = createLogger('LogbookRepository');

export class LogbookRepository extends BaseRepository<LogbookEntry> {
  constructor() {
    super(db.logbook_entries, 'logbook_entries');
  }

  // ============================================================================
  // BASIC QUERIES
  // ============================================================================

  /**
   * Find logbook entries by ship
   */
  async findByShip(shipId: string, options?: QueryOptions): Promise<QueryResult<LogbookEntry>> {
    return await this.findAll({
      ...options,
      filters: { ...options?.filters, shipId }
    });
  }

  /**
   * Find logbook entries by type
   */
  async findByType(logbookType: LogbookType, options?: QueryOptions): Promise<QueryResult<LogbookEntry>> {
    return await this.findAll({
      ...options,
      filters: { ...options?.filters, logbookType }
    });
  }

  /**
   * Find logbook entries by status
   */
  async findByStatus(status: LogbookEntryStatus, options?: QueryOptions): Promise<QueryResult<LogbookEntry>> {
    return await this.findAll({
      ...options,
      filters: { ...options?.filters, status }
    });
  }

  /**
   * Find logbook entries by watch keeper
   */
  async findByWatchKeeper(watchKeeperId: string, options?: QueryOptions): Promise<QueryResult<LogbookEntry>> {
    return await this.findAll({
      ...options,
      filters: { ...options?.filters, watchKeeperId }
    });
  }

  /**
   * Find logbook entries by organization
   */
  async findByOrganization(organizationId: string, options?: QueryOptions): Promise<QueryResult<LogbookEntry>> {
    return await this.findAll({
      ...options,
      filters: { ...options?.filters, organizationId }
    });
  }

  /**
   * Find logbook entries by watch period
   */
  async findByWatchPeriod(watchPeriodId: string, options?: QueryOptions): Promise<QueryResult<LogbookEntry>> {
    return await this.findAll({
      ...options,
      filters: { ...options?.filters, watchPeriodId }
    });
  }

  // ============================================================================
  // ADVANCED QUERIES
  // ============================================================================

  /**
   * Find logbook entries by date range
   */
  async findByDateRange(
    shipId: string,
    startDate: Date,
    endDate: Date,
    logbookType?: LogbookType
  ): Promise<LogbookEntry[]> {
    let query = this.table
      .where('vesselId')
      .equals(shipId)
      .and(entry => {
        const entryDate = new Date(entry.entryDate);
        return entryDate >= startDate && entryDate <= endDate;
      });

    if (logbookType) {
      query = query.and(entry => entry.logbookType === logbookType);
    }

    return await query.sortBy('entryDate');
  }

  /**
   * Find entries by ship and type with date range
   */
  async findByShipAndType(
    shipId: string,
    logbookType: LogbookType,
    startDate?: Date,
    endDate?: Date
  ): Promise<LogbookEntry[]> {
    let query = this.table
      .where('[shipId+logbookType]')
      .equals([shipId, logbookType]);

    if (startDate || endDate) {
      query = query.and(entry => {
        const entryDate = new Date(entry.entryDate);
        if (startDate && entryDate < startDate) return false;
        if (endDate && entryDate > endDate) return false;
        return true;
      });
    }

    return await query.sortBy('entryDate');
  }

  /**
   * Find recent entries for a ship
   */
  async findRecent(shipId: string, limit: number = 10): Promise<LogbookEntry[]> {
    return await this.table
      .where('vesselId')
      .equals(shipId)
      .reverse()
      .sortBy('createdAt')
      .then(entries => entries.slice(0, limit));
  }

  /**
   * Find entries requiring approval
   */
  async findPendingApproval(shipId: string): Promise<LogbookEntry[]> {
    return await this.table
      .where('vesselId')
      .equals(shipId)
      .and(entry => entry.status === 'submitted' || entry.status === 'reviewed')
      .sortBy('submittedAt');
  }

  /**
   * Find overdue entries (not approved within 24 hours)
   */
  async findOverdue(shipId: string): Promise<LogbookEntry[]> {
    const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    
    return await this.table
      .where('vesselId')
      .equals(shipId)
      .and(entry => {
        if (entry.status === 'approved' || entry.status === 'locked') return false;
        const createdAt = new Date(entry.createdAt);
        return createdAt < twentyFourHoursAgo;
      })
      .sortBy('createdAt');
  }

  /**
   * Find locked entries
   */
  async findLocked(shipId: string): Promise<LogbookEntry[]> {
    return await this.table
      .where('vesselId')
      .equals(shipId)
      .and(entry => entry.status === 'locked' && entry.lockedAt !== undefined)
      .sortBy('lockedAt');
  }

  /**
   * Get next entry number for a logbook type
   */
  async getNextEntryNumber(shipId: string, logbookType: LogbookType): Promise<number> {
    const entries = await this.table
      .where('[shipId+logbookType]')
      .equals([shipId, logbookType])
      .toArray();

    if (entries.length === 0) return 1;

    const maxEntry = Math.max(...entries.map(e => e.entryNumber));
    return maxEntry + 1;
  }

  // ============================================================================
  // FILTERING
  // ============================================================================

  /**
   * Apply complex filters to logbook entries
   */
  async applyFilters(filters: LogbookFilters): Promise<LogbookEntry[]> {
    let query = this.table.toCollection();

    // Filter by ship
    if (filters.vesselId) {
      query = query.and(entry => entry.vesselId === filters.vesselId);
    }

    // Filter by logbook type
    if (filters.logbookType) {
      query = query.and(entry => entry.logbookType === filters.logbookType);
    }

    // Filter by status
    if (filters.status) {
      query = query.and(entry => entry.status === filters.status);
    }

    // Filter by date range
    if (filters.dateFrom || filters.dateTo) {
      query = query.and(entry => {
        const entryDate = new Date(entry.entryDate);
        if (filters.dateFrom && entryDate < filters.dateFrom) return false;
        if (filters.dateTo && entryDate > filters.dateTo) return false;
        return true;
      });
    }

    // Filter by watch period
    if (filters.watchPeriodId) {
      query = query.and(entry => entry.watchPeriodId === filters.watchPeriodId);
    }

    // Filter by watch keeper
    if (filters.watchKeeperId) {
      query = query.and(entry => entry.watchKeeperId === filters.watchKeeperId);
    }

    // Filter by creator
    if (filters.createdBy) {
      query = query.and(entry => entry.createdBy === filters.createdBy);
    }

    // Filter by approver
    if (filters.approvedBy) {
      query = query.and(entry => entry.approvedBy === filters.approvedBy);
    }

    // Filter by search term
    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      query = query.and(entry => {
        return (
          entry.description?.toLowerCase().includes(term) ||
          entry.remarks?.toLowerCase().includes(term) ||
          entry.watchKeeperName?.toLowerCase().includes(term) ||
          entry.entryNumber.toString().includes(term)
        );
      });
    }

    return await query.sortBy('entryDate').then(entries => entries.reverse());
  }

  // ============================================================================
  // SEARCH
  // ============================================================================

  /**
   * Full-text search across logbook entries
   */
  async search(shipId: string, searchTerm: string): Promise<LogbookEntry[]> {
    const term = searchTerm.toLowerCase();

    return await this.table
      .where('vesselId')
      .equals(shipId)
      .filter(entry => {
        // Search in common fields
        if (entry.description?.toLowerCase().includes(term)) return true;
        if (entry.remarks?.toLowerCase().includes(term)) return true;
        if (entry.watchKeeperName?.toLowerCase().includes(term)) return true;
        if (entry.entryNumber.toString().includes(term)) return true;

        // Search in type-specific data
        if (entry.deckLogData) {
          const deckData = JSON.stringify(entry.deckLogData).toLowerCase();
          if (deckData.includes(term)) return true;
        }

        if (entry.engineLogData) {
          const engineData = JSON.stringify(entry.engineLogData).toLowerCase();
          if (engineData.includes(term)) return true;
        }

        if (entry.orbData) {
          if (entry.orbData.portName?.toLowerCase().includes(term)) return true;
          if (entry.orbData.operationDescription?.toLowerCase().includes(term)) return true;
        }

        if (entry.grbData) {
          if (entry.grbData.portName?.toLowerCase().includes(term)) return true;
          if (entry.grbData.categoryDescription?.toLowerCase().includes(term)) return true;
        }

        return false;
      })
      .toArray();
  }

  // ============================================================================
  // WORKFLOW OPERATIONS
  // ============================================================================

  /**
   * Submit entry for review
   */
  async submit(id: string, submittedBy: string): Promise<LogbookEntry> {
    const entry = await this.findById(id);
    if (!entry) throw new Error('Entry not found');
    
    if (entry.status !== 'draft') {
      throw new Error('Only draft entries can be submitted');
    }

    return await this.update(id, {
      status: 'submitted',
      submittedAt: new Date(),
      submittedBy,
      updatedAt: new Date()
    });
  }

  /**
   * Review entry
   */
  async review(id: string, reviewedBy: string): Promise<LogbookEntry> {
    const entry = await this.findById(id);
    if (!entry) throw new Error('Entry not found');
    
    if (entry.status !== 'submitted') {
      throw new Error('Only submitted entries can be reviewed');
    }

    return await this.update(id, {
      status: 'reviewed',
      reviewedAt: new Date(),
      reviewedBy,
      updatedAt: new Date()
    });
  }

  /**
   * Approve entry
   */
  async approve(id: string, approvedBy: string, signature?: string): Promise<LogbookEntry> {
    const entry = await this.findById(id);
    if (!entry) throw new Error('Entry not found');
    
    if (entry.status !== 'submitted' && entry.status !== 'reviewed') {
      throw new Error('Only submitted or reviewed entries can be approved');
    }

    return await this.update(id, {
      status: 'approved',
      approvedAt: new Date(),
      approvedBy,
      approverSignature: signature,
      updatedAt: new Date()
    });
  }

  /**
   * Reject entry
   */
  async reject(id: string, reason: string): Promise<LogbookEntry> {
    const entry = await this.findById(id);
    if (!entry) throw new Error('Entry not found');
    
    if (entry.status === 'locked') {
      throw new Error('Locked entries cannot be rejected');
    }

    return await this.update(id, {
      status: 'rejected',
      rejectionReason: reason,
      updatedAt: new Date()
    });
  }

  /**
   * Lock entry (prevents further edits)
   */
  async lock(id: string): Promise<LogbookEntry> {
    const entry = await this.findById(id);
    if (!entry) throw new Error('Entry not found');
    
    if (entry.status !== 'approved') {
      throw new Error('Only approved entries can be locked');
    }

    if (entry.lockedAt) {
      throw new Error('Entry is already locked');
    }

    return await this.update(id, {
      status: 'locked',
      lockedAt: new Date(),
      updatedAt: new Date()
    });
  }

  // ============================================================================
  // BULK OPERATIONS
  // ============================================================================

  /**
   * Bulk create entries (for importing historical data)
   */
  async bulkCreate(entries: Partial<LogbookEntry>[]): Promise<LogbookEntry[]> {
    const records: LogbookEntry[] = [];

    for (const entry of entries) {
      // Generate ID if not provided
      const id = entry.id || this.generateId();
      
      // Set default timestamps
      const now = new Date();
      const record: LogbookEntry = {
        ...entry as LogbookEntry,
        id,
        createdAt: entry.createdAt || now,
        updatedAt: entry.updatedAt || now,
        syncStatus: entry.syncStatus || 'pending'
      };

      records.push(record);
    }

    // Bulk add to database
    await this.table.bulkAdd(records);
    
    return records;
  }

  /**
   * Bulk update entries
   */
  async bulkUpdate(updates: Array<{ id: string; data: Partial<LogbookEntry> }>): Promise<number> {
    let updated = 0;

    for (const { id, data } of updates) {
      try {
        await this.update(id, data);
        updated++;
      } catch (error) {
        logger.error('Failed to update entry', { id, error });
      }
    }

    return updated;
  }

  /**
   * Bulk delete entries
   */
  async bulkDelete(ids: string[]): Promise<number> {
    await this.table.bulkDelete(ids);
    return ids.length;
  }

  /**
   * Import entries from JSON
   */
  async importFromJSON(jsonData: string): Promise<{ imported: number; errors: string[] }> {
    const errors: string[] = [];
    let imported = 0;

    try {
      const data = JSON.parse(jsonData);
      const entries = Array.isArray(data) ? data : [data];

      for (const entry of entries) {
        try {
          await this.create(entry as LogbookEntry);
          imported++;
        } catch (error) {
          errors.push(`Failed to import entry: ${(error as Error).message}`);
        }
      }
    } catch (error) {
      errors.push(`JSON parsing failed: ${(error as Error).message}`);
    }

    return { imported, errors };
  }

  // ============================================================================
  // STATISTICS
  // ============================================================================

  /**
   * Count entries by status
   */
  async countByStatus(shipId: string): Promise<Record<LogbookEntryStatus, number>> {
    const entries = await this.table
      .where('vesselId')
      .equals(shipId)
      .toArray();

    const counts: Partial<Record<LogbookEntryStatus, number>> = {};
    
    for (const entry of entries) {
      counts[entry.status] = (counts[entry.status] || 0) + 1;
    }

    return counts as Record<LogbookEntryStatus, number>;
  }

  /**
   * Count entries by type
   */
  async countByType(shipId: string): Promise<Record<LogbookType, number>> {
    const entries = await this.table
      .where('vesselId')
      .equals(shipId)
      .toArray();

    const counts: Partial<Record<LogbookType, number>> = {};
    
    for (const entry of entries) {
      counts[entry.logbookType] = (counts[entry.logbookType] || 0) + 1;
    }

    return counts as Record<LogbookType, number>;
  }

  /**
   * Get completion rate for a ship
   */
  async getCompletionRate(shipId: string): Promise<number> {
    const entries = await this.table
      .where('vesselId')
      .equals(shipId)
      .toArray();

    if (entries.length === 0) return 100;

    const approved = entries.filter(e => e.status === 'approved' || e.status === 'locked').length;
    return Math.round((approved / entries.length) * 100);
  }
}

export const logbookRepository = new LogbookRepository();


